// The rules of Review and fix (release 3.3, import redesign I4; plan 4.5), as
// plain functions the node tests call without drawing anything.
//
// # WHY A GRID
//
// Review and fix was a table of tall rows (about 170 px each): a text block of
// up to five "Label: value" lines, a "To fix" form and a "Hold this vendor"
// button that wrapped inside its word. A file of 742 vendors was 37 pages of
// that. Now it is a grid like the spreadsheet the file came from: one column
// per imported field, one row per vendor, 36 px rows, 50 rows a page read from
// the server (the server pages; the page never holds the whole file), the rows
// with problems first. A problem is a cell, said in one short sentence, with
// its fix beside it.
//
// # WHAT IS NEVER DRAWN
//
// A field key, an option code, a duplicate answer's code: the grid works on
// the API's labels and sends the codes back by index, as every step does.

import type {
  ImportAnalysis,
  ImportColumnChoices,
  ImportCounts,
  ImportDuplicates,
  ImportField,
  ImportOption,
  ImportPreviewFilter,
  ImportPreviewRow,
  ImportProblem,
} from '../../lib/api'
import { KEEP_EXTRA, LEAVE_OUT, plural } from '../../lib/importWords'

// Rows a page, read from the server. The plan's figure: 50, in a panel no
// taller than the window.
export const REVIEW_PAGE = 50

// "If a vendor exists": the duplicate rule, one select above the grid. The
// first is the default.
export const DUPLICATE_RULES: { code: ImportDuplicates; label: string }[] = [
  { code: 'FILL_EMPTY', label: 'Fill empty fields' },
  { code: 'SKIP', label: 'Leave them alone' },
  { code: 'UPDATE', label: 'Update from the file' },
]

// The one line that says nothing is written yet, said once on the step.
export const NOTHING_SAVED = 'Nothing is saved to the register until you import.'

// ------------------------------------------------------------- the columns

// One column of the grid: a column of the file that imports into a field of
// ours. The vendor name comes first, then the rest in the file's order.
// Columns kept as extra fields or left out are not in the grid: it shows what
// the register will hold.
export type GridColumn = {
  column: string
  field: string
  label: string
  name: boolean
  // More than one column of the file goes to this field (two GSTIN columns):
  // the register holds them as one value, so each grid column shows its own
  // file value, and a fix is made where it was typed.
  shared: boolean
}

export function gridColumns(analysis: Pick<ImportAnalysis, 'columns' | 'fields'>, choices: ImportColumnChoices): GridColumn[] {
  const byKey = new Map(analysis.fields.map((f) => [f.key, f]))
  const nameKey = nameField(analysis.fields)
  const out: GridColumn[] = []
  for (const c of [...analysis.columns].sort((a, b) => a.index - b.index)) {
    const choice = choices[c.column] ?? ''
    if (choice === KEEP_EXTRA || choice === LEAVE_OUT) continue
    const f = byKey.get(choice)
    if (!f) continue
    // The second column going to a field is named by its file column too.
    const seen = out.find((g) => g.field === f.key)
    if (seen) seen.shared = true
    out.push({
      column: c.column,
      field: f.key,
      label: seen ? f.label + ' (' + c.column + ')' : f.label,
      name: f.key === nameKey && !seen,
      shared: Boolean(seen),
    })
  }
  const first = out.findIndex((g) => g.name)
  if (first > 0) out.unshift(...out.splice(first, 1))
  return out
}

// The field that names a vendor: the one of kind NAME, else the first required.
export function nameField(fields: ImportField[]): string {
  return (fields.find((f) => f.kind === 'NAME') ?? fields.find((f) => f.required))?.key ?? ''
}

// --------------------------------------------------------------- the cells

// The problems of a row that sit in one grid column: a problem with a file
// column sits in that column; one with no column (a choice for the field
// itself) sits in every column that goes to its field.
export function problemsIn(row: Pick<ImportPreviewRow, 'problems'>, col: GridColumn): ImportProblem[] {
  return (row.problems ?? []).filter((p) => (p.column !== '' ? p.column === col.column : p.field === col.field))
}

export type CellView = {
  // What the cell shows: the value as the register will hold it when the
  // engine settled one, else what the file (or a fix) says.
  text: string
  // The whole of it, for the tooltip: no cell is cut short without one.
  title: string
  // What the file's cell says now: the person's fix if there is one.
  file: string
  fixed: boolean
  problems: ImportProblem[]
}

export function cellView(row: ImportPreviewRow, col: GridColumn): CellView {
  const raw = row.raw?.[col.column] ?? ''
  const fixed = row.fixes?.[col.column] !== undefined
  const file = fixed ? (row.fixes[col.column] ?? '') : raw
  const cells = (row.cells ?? []).filter((c) => c.field === col.field)
  const problems = problemsIn(row, col)
  const landed = cells.length === 1 && !col.shared ? cells[0] : undefined
  const text = problems.length === 0 && landed && landed.value !== '' ? landed.value : file
  const lines: string[] = []
  if (text !== '') lines.push(text)
  if (landed && landed.value !== '' && landed.value !== file && file !== '') lines.push('Your file: ' + file)
  if (fixed && raw !== file) lines.push(raw === '' ? 'Empty in your file' : 'Changed from your file: ' + raw)
  if (landed?.note) lines.push(landed.note)
  for (const p of problems) lines.push(p.message)
  return { text, title: lines.join('\n'), file, fixed, problems }
}

// The first sentence of an engine message: what the grid says in a 36 px row.
// The rest is in the tooltip. A full stop inside a quoted value from the file
// does not end the sentence: 'Constitution "Pvt. Ltd" is not one we know.' was
// cut to 'Constitution "Pvt.' (I4 review, 13 Oct 2026). A sentence may end
// inside the quotes ('The file says "Pvt. Ltd."'), at the closing quote. With
// the quotes unbalanced the first stop ends it, as before.
export function firstSentence(message: string): string {
  const stop = (ch: string | undefined) => ch === '.' || ch === '?' || ch === '!'
  const gap = (ch: string | undefined) => ch === undefined || /\s/.test(ch)
  if ((message.match(/"/g) ?? []).length % 2 === 0) {
    let quoted = false
    for (let i = 0; i < message.length; i++) {
      const ch = message[i]
      if (ch === '"') {
        quoted = !quoted
        if (!quoted && stop(message[i - 1]) && gap(message[i + 1])) return message.slice(0, i + 1).trim()
      } else if (!quoted && stop(ch) && gap(message[i + 1])) {
        return message.slice(0, i + 1).trim()
      }
    }
    return message.trim()
  }
  const m = message.match(/^(.+?[.?!])(\s|$)/)
  return (m?.[1] ?? message).trim()
}

// -------------------------------------------------------------- the fixes

// How a problem is fixed in place:
//  - "duplicate": the row looks like a vendor already on the register. The
//    answers are buttons (Yes, update / No, add as new) beside Skip.
//  - "choice": a choice for the field itself. Up to 3 options are buttons;
//    more are the likely ones as buttons ("Use Private limited company") and
//    the rest in a list.
//  - "text": the file's cell is wrong; the fix is to edit the cell.
export type FixKind = 'duplicate' | 'choice' | 'text'

export function fixKind(p: Pick<ImportProblem, 'field' | 'column' | 'input' | 'options'>): FixKind {
  if (p.column === '' && p.field === 'match') return 'duplicate'
  if ((p.options ?? []).length > 0 && (p.column === '' || p.input === 'choice')) return 'choice'
  return 'text'
}

// The short words for a duplicate answer, by what the answer does. The codes
// are the importer's (universal.go: MatchSame and the rest); they choose the
// words and are never drawn. An answer this console does not know keeps the
// API's own label.
const DUPLICATE_WORDS: Record<string, string> = {
  SAME: 'Yes, update',
  SAME_NO_IDS: 'Yes, keep its PAN and GSTIN',
  NEW: 'No, add as new',
}

export function duplicateAnswers(options: ImportOption[]): { index: number; label: string; title: string }[] {
  const order = (o: ImportOption) => (o.code === 'NEW' ? 1 : 0)
  return options
    .map((o, index) => ({ index, label: DUPLICATE_WORDS[o.code] ?? o.label, title: o.label, o }))
    .sort((a, b) => order(a.o) - order(b.o))
    .map(({ index, label, title }) => ({ index, label, title }))
}

// The options most like what the file says, best first, for "Use ..." buttons:
// the same word (3), a word written short ("Pvt" for "Private": the same
// first letter and the rest in order, 2), or the option's initials run
// together ("LLP", 4). An option that shares nothing is not likely, and a
// guess is only ever a button the person presses: nothing is chosen for them.
export function likelyOptions(fileValue: string, options: ImportOption[], max = 2): number[] {
  const words = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9 ]+/g, ' ')
      .split(/\s+/)
      .filter((w) => w.length > 0)
  const shortFor = (w: string, t: string) => {
    if (w.length < 2 || w[0] !== t[0]) return false
    let at = 0
    for (const ch of t) if (ch === w[at]) at++
    return at === w.length
  }
  const mine = words(fileValue)
  if (mine.length === 0) return []
  const joined = mine.join('')
  const scored = options.map((o, i) => {
    const theirs = words(o.label)
    if (theirs.length === 0) return { i, s: 0 }
    let s = 0
    for (const w of mine) {
      if (theirs.includes(w)) s += 3
      else if (theirs.some((t) => shortFor(w, t))) s += 2
    }
    const initials = theirs.map((t) => t[0]).join('')
    if (joined.length >= 2 && (initials === joined || theirs.join('') === joined)) s += 4
    return { i, s }
  })
  return scored
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .slice(0, max)
    .map((x) => x.i)
}

// What the file says for a problem's field: its own column, or the column the
// person matched to the field. "" when neither is known.
export function fileValueFor(row: Pick<ImportPreviewRow, 'raw' | 'fixes'>, p: ImportProblem, choices: ImportColumnChoices): string {
  const column = p.column !== '' ? p.column : (Object.keys(choices).find((c) => choices[c] === p.field) ?? '')
  if (!column) return ''
  return row.fixes?.[column] ?? row.raw?.[column] ?? ''
}

// ---------------------------------------------------- the counts and filter

// The filter the step opens on: the problems first, when there are any.
export function firstFilter(c: ImportCounts): ImportPreviewFilter {
  return c.attention > 0 ? 'attention' : 'all'
}

// The counts are the filter: one segmented control. Vendors already on the
// register (updated, or left alone under the duplicate rule) are one
// segment, because the server filters them together.
export function filterTiles(c: ImportCounts): { filter: ImportPreviewFilter; label: string; n: number }[] {
  return [
    { filter: 'all', label: 'All', n: c.total },
    { filter: 'add', label: 'New', n: c.add },
    { filter: 'existing', label: 'On the register', n: c.update + c.leftAlone },
    { filter: 'attention', label: 'To fix', n: c.attention },
    { filter: 'held', label: 'On hold', n: c.held },
  ]
}

// The sticky footer's one line: what the Import button will do.
export function footerWords(c: ImportCounts): string {
  const going = c.add + c.update
  const skip = Math.max(0, c.total - going)
  return 'Will import ' + plural(going, 'vendor') + (skip > 0 ? ', skip ' + skip.toLocaleString('en-IN') : '') + '.'
}

// ------------------------------------------------------------- the pager

// The page numbers to offer: the first, the last, and the pages either side
// of the current one, with a gap ("...") between runs. Pages count from 1.
export function pageList(current: number, pages: number): (number | '...')[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1)
  const want = new Set([1, pages, current - 1, current, current + 1])
  if (current <= 3) [2, 3, 4].forEach((n) => want.add(n))
  if (current >= pages - 2) [pages - 3, pages - 2, pages - 1].forEach((n) => want.add(n))
  const list = [...want].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b)
  const out: (number | '...')[] = []
  list.forEach((n, i) => {
    if (i > 0 && n - list[i - 1]! > 1) out.push('...')
    out.push(n)
  })
  return out
}

// "Rows 51-100 of 742".
export function rangeWords(offset: number, shown: number, total: number): string {
  if (total === 0 || shown === 0) return 'No rows'
  const f = (n: number) => n.toLocaleString('en-IN')
  return 'Rows ' + f(offset + 1) + '-' + f(offset + shown) + ' of ' + f(total)
}

// ------------------------------------- Back to Match columns, and forward

// The columns that carry fixes and are matched differently now than when the
// fixes were made. The server keeps every fix beside the file and checks every
// row again whenever the matching is applied (importer universal.go: nothing a
// person changed is lost), so nothing is cleared; this names what changed
// under the fixes, once.
export function changedFixedColumns(
  before: ImportColumnChoices,
  after: ImportColumnChoices,
  fixed: Iterable<string>,
): string[] {
  const out: string[] = []
  for (const c of fixed) {
    if (c.startsWith('@')) continue
    if ((before[c] ?? KEEP_EXTRA) !== (after[c] ?? KEEP_EXTRA) && !out.includes(c)) out.push(c)
  }
  return out.sort()
}

// The one line for those columns, or "" when there are none.
export function fixNoteWords(columns: string[], after: ImportColumnChoices): string {
  if (columns.length === 0) return ''
  const list = columns.length === 1 ? columns[0]! : columns.slice(0, -1).join(', ') + ' and ' + columns[columns.length - 1]
  const out = columns.filter((c) => after[c] === LEAVE_OUT)
  if (out.length === columns.length) {
    return (columns.length === 1 ? list + ' is' : list + ' are') + ' not imported now, so the fixes in ' + (columns.length === 1 ? 'it' : 'them') + ' are not used.'
  }
  return (columns.length === 1 ? list + ' is' : list + ' are') + ' matched differently now. The fixes in ' + (columns.length === 1 ? 'it' : 'them') + ' are kept and checked again.'
}
